import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { useLanguage } from "@/contexts/LanguageContext";
import { Menu, X, Phone, Globe } from "lucide-react";

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const { t, lang, changeLang, languageNames, languages } = useLanguage();
  const isAdmin = location.pathname.startsWith("/admin");
  const isPatient = location.pathname === "/patient" || location.pathname.startsWith("/patient/");
  const isSplash = location.pathname === "/splash";
  const isPartner = location.pathname === "/partners";
  const isPartnerPortal = location.pathname.startsWith("/partner");

  if (isAdmin || isPatient || isSplash || isPartner || isPartnerPortal) return null;

  const navLinks = [
    { label: t("nav.home"), href: "/" },
    { label: t("nav.about"), href: "/about" },
    { label: t("nav.services"), href: "/services" },
    { label: t("nav.treatments"), href: "/treatments" },
    { label: t("nav.guide"), href: "/guide" },
    { label: t("nav.blog"), href: "/blog" },
    { label: t("nav.faq"), href: "/faq" },
  ];

  return (
    <nav
      data-testid="main-navbar"
      className="sticky top-0 z-50 backdrop-blur-xl bg-white/80 border-b border-slate-200/60"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
            <img src="/logo.png" alt="Flarial MediTours" className="w-14 h-14 lg:w-16 lg:h-16 object-contain" />
            <div className="flex flex-col leading-tight">
              <span className="font-outfit font-semibold text-lg">
                <span className="text-[#193c6b]">Flarial</span> <span className="text-[#8bc53f]">MediTours</span>
              </span>
              <span className="text-[10px] sm:text-[11px] text-slate-500 tracking-wide hidden sm:block">
                Expert Care. Effortless Journey.
              </span>
            </div>
          </Link>

          <div className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                data-testid={`nav-${link.href.replace("/", "") || "home"}`}
                className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                  location.pathname === link.href
                    ? "text-[#00A4A9] bg-[#e0f2fe]"
                    : "text-slate-600 hover:text-[#03045e] hover:bg-slate-50"
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          <div className="hidden lg:flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="rounded-full text-slate-600 hover:text-[#03045e] gap-1.5" data-testid="language-switcher">
                  <Globe className="w-4 h-4" />
                  <span className="text-xs font-medium">{lang.toUpperCase()}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="min-w-[140px]">
                {languages.map((l) => (
                  <DropdownMenuItem
                    key={l}
                    onClick={() => changeLang(l)}
                    className={`cursor-pointer ${l === lang ? "bg-[#e0f2fe] text-[#00A4A9]" : ""}`}
                    data-testid={`lang-${l}`}
                  >
                    {languageNames[l]}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            <Link to="/contact" data-testid="nav-contact-link">
              <Button
                variant="outline"
                size="sm"
                className="rounded-full border-slate-300 text-slate-600 hover:border-[#00A4A9] hover:text-[#00A4A9]"
              >
                <Phone className="w-4 h-4 mr-1.5" />
                {t("nav.contact")}
              </Button>
            </Link>
            <Link to="/contact" data-testid="nav-get-quote-btn">
              <Button
                size="sm"
                className="rounded-full bg-[#00A4A9] hover:bg-[#008084] text-white btn-hover"
              >
                {t("nav.getQuote")}
              </Button>
            </Link>
          </div>

          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild className="lg:hidden">
              <Button variant="ghost" size="icon" data-testid="mobile-menu-btn">
                {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-72 pt-12">
              <div className="flex flex-col gap-1">
                {navLinks.map((link) => (
                  <Link
                    key={link.href}
                    to={link.href}
                    onClick={() => setOpen(false)}
                    data-testid={`mobile-nav-${link.href.replace("/", "") || "home"}`}
                    className={`px-4 py-3 text-sm font-medium rounded-lg transition-colors ${
                      location.pathname === link.href
                        ? "text-[#00A4A9] bg-[#e0f2fe]"
                        : "text-slate-600 hover:bg-slate-50"
                    }`}
                  >
                    {link.label}
                  </Link>
                ))}
                <div className="border-t border-slate-200 my-3" />
                <div className="px-4 mb-3">
                  <p className="text-xs text-slate-400 mb-2">Language</p>
                  <div className="flex flex-wrap gap-1.5">
                    {languages.map((l) => (
                      <Button
                        key={l}
                        variant={l === lang ? "default" : "outline"}
                        size="sm"
                        className={`rounded-full text-xs ${l === lang ? "bg-[#00A4A9] hover:bg-[#008084]" : ""}`}
                        onClick={() => changeLang(l)}
                        data-testid={`mobile-lang-${l}`}
                      >
                        {l.toUpperCase()}
                      </Button>
                    ))}
                  </div>
                </div>
                <Link to="/contact" onClick={() => setOpen(false)}>
                  <Button className="w-full rounded-full bg-[#00A4A9] hover:bg-[#008084] text-white" data-testid="mobile-get-quote-btn">
                    {t("nav.getQuote")}
                  </Button>
                </Link>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </nav>
  );
}
